import React, { useCallback, useMemo } from 'react';
import Button, { IconButton } from '@atlaskit/button/new';
import MegaphoneIcon from '@atlaskit/icon/core/megaphone';
import Link from '@atlaskit/link';
import Tooltip from '@atlaskit/tooltip';
import { Text } from '@atlaskit/primitives/compiled';
import { expVal } from '@atlassian/jira-feature-experiments';
import { fg } from '@atlassian/jira-feature-gating';
import { ENTRYPOINT_ID_NAVIGATION_REFRESH } from '@atlassian/jira-feedback-collector/src/constants.tsx';
import { useFeedbackCollectorActions } from '@atlassian/jira-feedback-collector/src/controllers/index.tsx';
import { useIntl, FormattedMessage } from '@atlassian/jira-intl';
import { NAV4_GENERAL_FEEDBACK_GROUP_ID } from '@atlassian/jira-navigation-apps-sidebar-nav4-sidebars-common-core/src/common/constants/index.tsx';
import normaliseUrl from '@atlassian/jira-platform-url-normalisation/src/index.tsx';
import { useCloudId } from '@atlassian/jira-tenant-context-controller/src/components/cloud-id/index.tsx';
import { useEnvironment } from '@atlassian/jira-tenant-context-controller/src/components/environment/index.tsx';
import { useLocale } from '@atlassian/jira-tenant-context-controller/src/components/locale/index.tsx';
import messages from './messages.tsx';

export function SidebarFeedbackButton(): React.JSX.Element {
	const environment = useEnvironment();
	const cloudId = useCloudId();
	const { formatMessage } = useIntl();

	const { openFeedbackCollector, closeFeedbackCollector } = useFeedbackCollectorActions();

	const formattedMessageNode = useMemo(() => {
		return (
			<>
				<Text as="p">
					<FormattedMessage {...messages.feedbackTitleDetailsNoLink} />
				</Text>
				<Link
					href="https://support.atlassian.com/jira-software-cloud/docs/what-is-the-new-navigation-in-jira/"
					onClick={closeFeedbackCollector}
					target="_blank"
				>
					<FormattedMessage {...messages.feedbackTitleLearnMoreLinkText} />
				</Link>
			</>
		);
	}, [closeFeedbackCollector]);

	const onClickHandler = useCallback(() => {
		openFeedbackCollector({
			entrypointId: ENTRYPOINT_ID_NAVIGATION_REFRESH,
			feedbackContext: [
				{
					id: 'environment',
					value: environment,
				},
				{
					id: 'cloud-id',
					value: cloudId,
				},
				{
					id: 'location',
					// eslint-disable-next-line jira/jira-ssr/no-unchecked-globals-usage
					value: normaliseUrl(window?.location?.href ?? ''),
				},
			],
			feedbackGroupId: NAV4_GENERAL_FEEDBACK_GROUP_ID,
			feedbackTitle: formatMessage(messages.feedbackTitle),
			feedbackTitleDetails: formattedMessageNode,
		});
	}, [cloudId, environment, formatMessage, formattedMessageNode, openFeedbackCollector]);

	const locale = useLocale();
	const showPersistentInvite =
		// Ensure that we can turn off the locale check with a feature gate if needed
		// eslint-disable-next-line jira/ff/no-preconditioning
		fg('navx_persistent_invite_jira_new_non_free') &&
		// Do not trigger an exposure on the experiment for non-English locales
		(locale?.toLowerCase().startsWith('en') ?? false) &&
		expVal('navx_persistent_invite_jira_new_non_free_exp', 'global_invite_enabled', false);

	return (
		<Tooltip content={formatMessage(messages.sidebarFeedbackButtonLabel)} position="top">
			{showPersistentInvite ||
			expVal('jfind-add-invite-button-to-left-nav-experiment', 'enrolled', false) ? (
				<IconButton
					icon={MegaphoneIcon}
					onClick={onClickHandler}
					label={formatMessage(messages.sidebarFeedbackButtonLabel)}
				/>
			) : (
				<Button iconBefore={MegaphoneIcon} appearance="subtle" onClick={onClickHandler}>
					{formatMessage(messages.sidebarFeedbackButtonLabel)}
				</Button>
			)}
		</Tooltip>
	);
}
